import { useEffect, useState } from 'react'
import { ArrowLeft, Download, ImagePlus, Loader2, RefreshCcw, Trash2, CheckCircle2, AlertTriangle, Database, Copy, Files } from 'lucide-react'
import { apiClient, ExternalAnalyzedImage, getApiError } from '../api'
import { Toast } from './Toast'
import { useTheme } from '../contexts/ThemeContext'

type PendingImage = { codigo: string; filename: string; imageData: string }

interface Props { onBack: () => void }

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result || ''))
    reader.onerror = () => reject(new Error(`Erro ao ler ${file.name}`))
    reader.readAsDataURL(file)
  })
}

export function ExternalUploadScreen({ onBack }: Props) {
  const { logo } = useTheme()
  const [pending, setPending] = useState<PendingImage[]>([])
  const [mode, setMode] = useState<'files' | 'one-to-many'>('files')
  const [sharedImage, setSharedImage] = useState<{ filename: string; imageData: string } | null>(null)
  const [codesText, setCodesText] = useState('')
  const [items, setItems] = useState<ExternalAnalyzedImage[]>([])
  const [busy, setBusy] = useState(false)
  const [toast, setToast] = useState<{message: string; type: 'error'|'success'|'info'} | null>(null)

  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(null), 2000)
    return () => clearTimeout(t)
  }, [toast])


  const parseCodes = (value: string) => {
    const seen = new Set<string>()
    return value
      .split(/[\s,;]+/)
      .map(code => code.trim())
      .filter(Boolean)
      .filter(code => {
        if (seen.has(code)) return false
        seen.add(code)
        return true
      })
  }

  const sharedCodes = parseCodes(codesText)

  const handleSharedImage = async (file?: File) => {
    if (!file) return
    try {
      if (!file.type.startsWith('image/')) throw new Error('Selecione um arquivo de imagem')
      if (file.size > 15 * 1024 * 1024) throw new Error(`${file.name} excede 15 MB`)
      setBusy(true)
      const imageData = await fileToDataUrl(file)
      setSharedImage({ filename: file.name, imageData })
      setPending([])
      setItems([])
    } catch (error) {
      setToast({ message: getApiError(error, 'Erro ao ler imagem'), type: 'error' })
    } finally {
      setBusy(false)
    }
  }

  const generateSharedCopies = async () => {
    try {
      if (!sharedImage) throw new Error('Selecione uma imagem primeiro')
      const codes = parseCodes(codesText)
      if (!codes.length) throw new Error('Informe pelo menos um código')
      if (codes.length > 100) throw new Error('Use no máximo 100 códigos por vez')
      const next: PendingImage[] = codes.map(codigo => ({
        codigo,
        filename: `${codigo}.jpg`,
        imageData: sharedImage.imageData
      }))
      setPending(next)
      setItems([])
      await analyze(next)
    } catch (error) {
      setToast({ message: getApiError(error, 'Erro ao gerar cópias'), type: 'error' })
    }
  }

  const switchMode = (nextMode: 'files' | 'one-to-many') => {
    if (busy) return
    setMode(nextMode)
    setPending([])
    setItems([])
    setSharedImage(null)
    setCodesText('')
  }

  const analyze = async (next: PendingImage[]) => {
    if (!next.length) return
    try {
      setBusy(true)
      const analyzed: ExternalAnalyzedImage[] = []
      for (let i = 0; i < next.length; i += 3) {
        const response = await apiClient.analyzeExternalImages(next.slice(i, i + 3))
        analyzed.push(...response.data.items)
      }
      setItems(analyzed)
      const failed = analyzed.filter(i => !i.valid).length
      setToast({ message: failed ? `${failed} imagem(ns) tiveram erro` : `${analyzed.length} imagem(ns) prontas`, type: failed ? 'info' : 'success' })
    } catch (error) {
      setToast({ message: getApiError(error, 'Erro ao analisar imagens'), type: 'error' })
    } finally { setBusy(false) }
  }

  const handleFiles = async (files: FileList | File[]) => {
    try {
      const imageFiles = Array.from(files).filter(f => f.type.startsWith('image/'))
      const tooLarge = imageFiles.find(f => f.size > 15 * 1024 * 1024)
      if (tooLarge) throw new Error(`${tooLarge.name} excede 15 MB`)
      if (!imageFiles.length) throw new Error('Selecione arquivos de imagem')
      if (imageFiles.length > 100) throw new Error('Envie no máximo 100 imagens por vez')
      setBusy(true)
      const next: PendingImage[] = []
      for (const file of imageFiles) {
        const codigo = file.name.replace(/\.[^.]+$/, '').trim()
        next.push({ codigo, filename: file.name, imageData: await fileToDataUrl(file) })
      }
      setPending(next)
      await analyze(next)
    } catch (error) {
      setToast({ message: getApiError(error, 'Erro ao ler imagens'), type: 'error' })
      setBusy(false)
    }
  }

  const reanalyze = async () => await analyze(pending)

  const changeCode = (index: number, codigo: string) => {
    setPending(prev => prev.map((item, i) => i === index ? { ...item, codigo } : item))
    setItems([])
  }

  const downloadZip = async () => {
    const valid = items.filter(i => i.valid && i.imageData).map(i => ({ codigo: i.codigo, filename: i.filename, imageData: i.imageData! }))
    if (!valid.length) return setToast({ message: 'Nenhuma imagem válida para baixar', type: 'error' })
    try {
      setBusy(true)
      const response = await apiClient.downloadExternalZip(valid)
      const url = URL.createObjectURL(response.data)
      const a = document.createElement('a')
      a.href = url
      a.download = 'imagens-importadas.zip'
      a.click()
      URL.revokeObjectURL(url)
      setToast({ message: 'ZIP gerado', type: 'success' })
    } catch (error) {
      setToast({ message: getApiError(error, 'Erro ao gerar ZIP'), type: 'error' })
    } finally { setBusy(false) }
  }

  const importToBank = async () => {
    const valid = items.filter(i => i.valid && i.imageData).map(i => ({ codigo: i.codigo, filename: i.filename, imageData: i.imageData! }))
    if (!valid.length) return setToast({ message: 'Nenhuma imagem válida para subir', type: 'error' })
    try {
      setBusy(true)
      const response = await apiClient.importExternalImages(valid)
      setToast({ message: response.data.message, type: 'success' })
      await analyze(pending)
    } catch (error) {
      setToast({ message: getApiError(error, 'Erro ao subir imagens'), type: 'error' })
    } finally { setBusy(false) }
  }

  const reset = () => { setPending([]); setItems([]); setSharedImage(null); setCodesText(''); setToast({ message: 'Tela limpa', type: 'info' }) }

  return (
    <div className="min-h-screen bg-gradient-light dark:bg-gradient-dark text-[#414142] dark:text-white">
      <Toast message={toast?.message || null} type={toast?.type} />
      <header className="sticky top-0 z-40 bg-white dark:bg-[#18181A] border-b border-gray-200 dark:border-[#5A5A5E]">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <img src={logo} className="h-9 w-auto" />
            <div><h1 className="font-bold text-lg sm:text-2xl">Importar imagens externas</h1><p className="text-xs text-gray-500 dark:text-gray-300">Nome do arquivo = código do produto • saída JPG até 100 KB</p></div>
          </div>
          <button onClick={onBack} className="px-3 py-2 rounded-lg bg-gray-100 dark:bg-[#232326] font-semibold text-sm flex items-center gap-2"><ArrowLeft size={16}/>Voltar</button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-3 sm:p-6 pb-28 sm:pb-6 space-y-5">
        <div className="bg-white dark:bg-[#232326] rounded-xl border border-gray-200 dark:border-[#5A5A5E] p-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
          <button
            onClick={() => switchMode('files')}
            disabled={busy}
            className={`px-4 py-3 rounded-lg font-bold flex items-center justify-center gap-2 transition ${mode === 'files' ? 'bg-[#E5191F] text-white dark:bg-[#FCD016] dark:text-[#18181A]' : 'bg-gray-100 text-gray-700 dark:bg-[#18181A] dark:text-gray-200'}`}
          >
            <Files size={18}/> Várias imagens por código
          </button>
          <button
            onClick={() => switchMode('one-to-many')}
            disabled={busy}
            className={`px-4 py-3 rounded-lg font-bold flex items-center justify-center gap-2 transition ${mode === 'one-to-many' ? 'bg-[#E5191F] text-white dark:bg-[#FCD016] dark:text-[#18181A]' : 'bg-gray-100 text-gray-700 dark:bg-[#18181A] dark:text-gray-200'}`}
          >
            <Copy size={18}/> 1 imagem → vários códigos
          </button>
        </div>

        {mode === 'files' ? (
          <label className="block border-2 border-dashed border-gray-300 dark:border-[#5A5A5E] rounded-xl p-8 text-center bg-white dark:bg-[#232326] hover:border-[#E5191F] cursor-pointer">
            <input type="file" accept="image/*" multiple className="hidden" disabled={busy} onChange={e => e.target.files && handleFiles(e.target.files)} />
            <ImagePlus size={36} className="mx-auto mb-3 text-[#E5191F] dark:text-[#FCD016]"/>
            <p className="font-bold">Clique para selecionar várias imagens</p>
            <p className="text-xs text-gray-500 mt-1">Nome do arquivo = código. PNG, WEBP, JPG etc. Transparência vira fundo branco automaticamente.</p>
          </label>
        ) : (
          <div className="bg-white dark:bg-[#232326] rounded-xl border border-gray-200 dark:border-[#5A5A5E] p-4 sm:p-5 space-y-4">
            <div>
              <h2 className="font-bold text-lg flex items-center gap-2"><Copy size={19} className="text-[#E5191F] dark:text-[#FCD016]"/>Duplicar uma imagem para vários códigos</h2>
              <p className="text-xs text-gray-500 dark:text-gray-300 mt-1">A mesma imagem será processada e gerada como um JPG separado para cada código informado.</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
              <label className="min-h-[220px] border-2 border-dashed border-gray-300 dark:border-[#5A5A5E] rounded-xl p-4 flex flex-col items-center justify-center text-center cursor-pointer hover:border-[#E5191F] bg-gray-50 dark:bg-[#18181A] overflow-hidden">
                <input type="file" accept="image/*" className="hidden" disabled={busy} onChange={e => handleSharedImage(e.target.files?.[0])} />
                {sharedImage ? (
                  <>
                    <img src={sharedImage.imageData} className="w-full h-36 object-contain bg-white rounded-lg border mb-3" />
                    <p className="text-xs font-semibold truncate max-w-full">{sharedImage.filename}</p>
                    <p className="text-[11px] text-gray-500 mt-1">Clique para trocar a imagem</p>
                  </>
                ) : (
                  <>
                    <ImagePlus size={38} className="mb-3 text-[#E5191F] dark:text-[#FCD016]"/>
                    <p className="font-bold">Selecionar imagem-base</p>
                    <p className="text-xs text-gray-500 mt-1">Uma única imagem para todos os códigos</p>
                  </>
                )}
              </label>

              <div className="space-y-3">
                <label className="block">
                  <span className="text-sm font-bold">Códigos dos produtos</span>
                  <textarea
                    value={codesText}
                    onChange={e => { setCodesText(e.target.value); setPending([]); setItems([]) }}
                    placeholder="13453, 412513, 4135"
                    rows={7}
                    className="mt-2 w-full px-3 py-3 rounded-xl border border-gray-300 dark:border-[#5A5A5E] bg-white dark:bg-[#18181A] font-mono text-sm resize-y focus:outline-none focus:ring-2 focus:ring-[#FCD016]"
                  />
                </label>
                <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
                  <span className="text-gray-500">Separe por vírgula, espaço, ponto e vírgula ou quebra de linha.</span>
                  <span className={`font-bold px-2 py-1 rounded-full ${sharedCodes.length ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>{sharedCodes.length} código(s)</span>
                </div>
                <button
                  onClick={generateSharedCopies}
                  disabled={busy || !sharedImage || sharedCodes.length === 0}
                  className="w-full rounded-xl px-4 py-3 bg-[#FCD016] text-[#18181A] font-bold flex items-center justify-center gap-2 shadow disabled:opacity-40"
                >
                  {busy ? <Loader2 size={17} className="animate-spin"/> : <Copy size={17}/>} Gerar e analisar {sharedCodes.length || ''} imagem(ns)
                </button>
              </div>
            </div>
          </div>
        )}

        {pending.length > 0 && (
          <div className="bg-white dark:bg-[#232326] rounded-xl border border-gray-200 dark:border-[#5A5A5E] overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-[#5A5A5E] flex flex-wrap items-center justify-between gap-2">
              <div><h2 className="font-bold">{mode === 'one-to-many' ? 'Cópias geradas' : 'Imagens carregadas'} ({pending.length})</h2><p className="text-xs text-gray-500">{mode === 'one-to-many' ? 'Cada código receberá uma cópia JPG da mesma imagem-base.' : 'Você pode corrigir o código e clicar em Analisar novamente.'}</p></div>
              <button onClick={reanalyze} disabled={busy} className="px-3 py-2 rounded-lg bg-gray-100 dark:bg-[#18181A] text-sm font-semibold flex items-center gap-2"><RefreshCcw size={14}/>Analisar</button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 p-4">
              {pending.map((p, idx) => {
                const result = items.find(i => i.filename === p.filename && i.codigo === p.codigo) || items[idx]
                return <div key={`${p.filename}-${idx}`} className="border border-gray-200 dark:border-[#5A5A5E] rounded-lg p-3 flex gap-3">
                  <img src={result?.imageData || p.imageData} className="w-20 h-20 object-contain bg-white rounded border" />
                  <div className="min-w-0 flex-1">
                    <input value={p.codigo} onChange={e => changeCode(idx, e.target.value)} className="w-full px-2 py-1 rounded border border-gray-300 dark:border-[#5A5A5E] dark:bg-[#18181A] font-mono font-bold text-sm" />
                    <p className="text-[11px] text-gray-500 truncate mt-1">{p.filename}</p>
                    {result && <div className="mt-2 flex flex-wrap gap-1">
                      {result.valid ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-700"><CheckCircle2 size={10} className="inline mr-1"/>{Math.round((result.sizeBytes || 0)/1024)} KB JPG</span> : <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-700"><AlertTriangle size={10} className="inline mr-1"/>Erro</span>}
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${result.productExists ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>{result.productExists ? 'Código no catálogo' : 'Fora do catálogo'}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${result.imageExists ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>{result.imageExists ? 'Já tem imagem' : 'Sem imagem no banco'}</span>
                    </div>}
                  </div>
                </div>
              })}
            </div>
          </div>
        )}

        {pending.length > 0 && <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sticky bottom-3">
          <button onClick={downloadZip} disabled={busy || !items.length} className="rounded-xl px-4 py-3 bg-slate-800 text-white font-bold flex items-center justify-center gap-2 shadow-lg disabled:opacity-50"><Download size={17}/>Baixar ZIP destas imagens</button>
          <button onClick={importToBank} disabled={busy || !items.length} className="rounded-xl px-4 py-3 bg-green-600 text-white font-bold flex items-center justify-center gap-2 shadow-lg disabled:opacity-50">{busy ? <Loader2 size={17} className="animate-spin"/> : <Database size={17}/>}Subir no banco</button>
          <button onClick={reset} disabled={busy} className="rounded-xl px-4 py-3 bg-red-600 text-white font-bold flex items-center justify-center gap-2 shadow-lg disabled:opacity-50"><Trash2 size={17}/>Excluir / resetar</button>
        </div>}
      </main>
    </div>
  )
}
